import { Theme, ButtonProps as MuiButtonProps } from '@mui/material';
import { globalClasses } from 'core/common/constants/global-classes';
import { AnswerButtonVariant } from 'core/flow/entities';

export enum ButtonAnimation {
  SHINE = 'shine',
}
declare module '@mui/material/Button' {
  interface ButtonPropsVariantOverrides {
    option: true;
    image: true;
    tertiary: true;
  }

  interface CustomButtonProps extends MuiButtonProps {
    gutterBottom?: boolean;
    animation?: ButtonAnimation;
    srcImage?: string;
  }
}

const getButtonAnimation = (animation?: ButtonAnimation) => {
  if (animation === ButtonAnimation.SHINE) {
    return {
      position: 'relative',
      overflow: 'hidden',
      '&::after': {
        content: '""',
        position: 'absolute',
        top: '-50%',
        left: '-50%',
        width: '200%',
        height: '200%',
        background: `linear-gradient(-120deg, transparent, rgba(255, 255, 255, 0.5), transparent)`,
        transform: 'translateX(-100%)',
        animation: `shine 3s ease-in-out infinite`,
      },
      '@keyframes shine': {
        '0%': {
          transform: 'translateX(-100%)',
          opacity: 0,
        },
        '10%': {
          opacity: 1,
        },
        '50%': {
          transform: 'translateX(100%)',
          opacity: 1,
        },
        '51%': {
          opacity: 0,
        },
        '100%': {
          transform: 'translateX(100%)',
          opacity: 0,
        },
      },
    };
  }
  return {};
};

export const getButtonOptions = ({ palette, breakpoints }: Theme) => {
  const buttonComponents = {
    styleOverrides: {
      root: ({
        ownerState,
      }: {
        ownerState: {
          gutterBottom?: boolean;
          animation?: ButtonAnimation;
          srcImage?: string;
          variant: AnswerButtonVariant;
        };
      }) => {
        const baseStyles = {
          backgroundPosition: 'center center',
          backgroundRepeat: 'no-repeat',
          backgroundSize: 'cover',
          textTransform: 'none',
          display: 'flex',
          marginBottom: ownerState.gutterBottom ? '12px' : 0,
        };

        const imageVariantStyles = {
          backgroundImage: ownerState.variant === 'image' && `url(${ownerState.srcImage})`,
        };

        return {
          ...baseStyles,
          ...imageVariantStyles,
          ...getButtonAnimation(ownerState.animation),
        };
      },
      sizeSmall: {
        padding: '4px 12px',
        fontSize: '12px',
        height: '32px',
        [breakpoints.up('mobile')]: {
          fontSize: '14px',
        },
      },
      sizeMedium: {
        padding: '8px 16px',
        fontSize: '14px',
        height: '40px',
        [breakpoints.up('mobile')]: {
          fontSize: '16px',
        },
      },
      sizeLarge: {
        padding: '12px 24px',
        fontSize: '16px',
        height: '52px',
        [breakpoints.up('mobile')]: {
          fontSize: '18px',
        },
      },
      option: {
        [`& ${globalClasses.buttonStartIcon}`]: {
          marginLeft: 0,
        },
      },
    },
    defaultProps: {
      disableElevation: true,
      size: 'large',
    },
    variants: [
      {
        props: { variant: 'text' },
        style: {
          color: palette.button.contrastTextButtonText,
          borderRadius: '30px',
          fontWeight: 600,
        },
      },
      {
        props: { variant: 'contained' },
        style: {
          backgroundColor: palette.button.main,
          color: palette.button.contrastMainText,
          borderRadius: '30px',
          fontWeight: 600,
          '&:hover': {
            backgroundColor: palette.button.mainHover,
            color: palette.button.contrastMainText,
          },
          '&:disabled': {
            backgroundColor: palette.button.mainDisabled,
            color: palette.button.contrastMainText,
          },
        },
      },
      {
        props: { variant: 'tertiary' },
        style: {
          backgroundColor: palette.button.tertiary,
          color: palette.button.contrastTertiaryText,
          borderRadius: '30px',
          fontWeight: 600,
          '&:hover': {
            backgroundColor: palette.button.tertiaryHover,
            color: palette.button.contrastTertiaryText,
          },
          '&:disabled': {
            backgroundColor: palette.button.tertiaryDisabled,
            color: palette.button.contrastTertiaryText,
          },
        },
      },
      {
        props: { variant: 'option' },
        style: {
          backgroundColor: palette.button.option,
          border: `1px solid ${palette.button.option}`,
          color: palette.button.contrastOptionText,
          borderRadius: '16px',
          justifyContent: 'flex-start',
          minHeight: '56px',
          boxShadow: palette.button.optionShadow,
          '&:hover': {
            border: `1px solid ${palette.button.optionHover}`,
            color: palette.button.contrastOptionText,
            backgroundColor: palette.button.option,
          },
          '&:disabled': {
            backgroundColor: palette.button.optionDisabled,
            color: palette.button.contrastOptionText,
          },
        },
      },
      {
        props: { variant: 'image' },
        style: {
          backgroundColor: palette.button.image,
          border: `1px solid ${palette.button.image}`,
          color: palette.button.contrastImageText,
          borderRadius: '16px',
          boxShadow: palette.button.imageShadow,
          minHeight: '108px',
          justifyContent: 'flex-start',
          '&:hover': {
            border: `1px solid ${palette.button.imageHover}`,
            color: palette.button.contrastImageText,
            backgroundColor: palette.button.image,
          },
          '&:disabled': {
            backgroundColor: palette.button.imageDisabled,
            color: palette.button.contrastImageText,
          },
        },
      },
    ],
  };

  return { buttonComponents };
};
